import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { backendErrorMessage } from '@core/proposals/backend-error';
import { ContainerTypesService } from '@core/container-types/container-types.service';
import { CapacityCellRef, capacityCellsForDryType, ContainerTypeRef } from '@core/offers/offer-containers';
import {
  buildContainerBlocks,
  buildContainerFreightOption,
  ContainerFreightBlock,
  ContainerFreightOptionView,
  defaultDestinationPortId,
  FreightDestinationPort,
  freightQuery,
  mayHaveMoreFreights,
  isValidContainerCount,
  legacyContainerPlan,
  LegacyContainerPlan,
  legacyFreightValuePerM3,
  LineForFreight,
  mainPrice,
  resolveFreightContainerTypeId,
  todayISO,
} from '@core/proposals/freight-options';
import {
  filterFreightByMode,
  FreightOption,
  ImportTaxCostInput,
  legacyVolumeM3,
  suggestImportTaxCost,
  suggestInsuranceCost,
  round2,
  suggestTransportCost,
  TransportMode,
} from '@core/proposals/pricing';
import { ProposalService } from '@core/proposals/proposal.service';
import { AdditionalCostType, CostType } from '@core/proposals/proposal.types';
import { SettingsService } from '@core/settings/settings.service';
import { ShippingService } from '@core/shipping/shipping.service';
import { SpeciesCapacityService } from '@core/species/species-capacity.service';
import { TranslocoService } from '@ngneat/transloco';
import { concatMap, debounceTime, from, Observable, of, Subject, take, takeUntil, tap, toArray } from 'rxjs';

/** One container type block of the Frete tab (container lines only). */
export interface ContainerBlockState {
  block: ContainerFreightBlock;
  /** Raw Freight rows of this container type for the offer origin port (server-side filtered). */
  options: any[];
  /** Same order as `options`: capacity, per m3 contribution for this line, expiry. */
  views: ContainerFreightOptionView[];
  selectedFreightId: number | null;
  /** Freight already written for this block during this dialog session (not re-sent on retry). */
  savedFreightId?: number | null;
  loading: boolean;
  /** The last query filled the take: there may be more freights than listed. */
  moreFreights?: boolean;
}

@Component({
  selector: 'app-dialog-add-cost',
  templateUrl: './dialog-add-cost.component.html',
  styleUrls: ['./dialog-add-cost.component.scss'],
})
export class DialogAddCostComponent implements OnInit, OnDestroy {
  activeTab: CostType = 'frete';
  transportMode: TransportMode = 'maritimo';
  selectedFreightId: number | null = null;
  freightOptions: any[] = [];
  containerBlocks: ContainerBlockState[] = [];
  /** Destination ports with freights from the origin port; the select shows them with "Todos" (null). */
  destinationOptions: FreightDestinationPort[] = [];
  /** Destination port filter of every freight query in this dialog; null = "Todos". */
  destinyPortId: number | null = null;
  /** Non container line: the last freight query filled the take. */
  legacyMoreFreights = false;
  /** Manual EUR/m3 freight value of NON container lines (D8). */
  legacyFreightValue: number | null = null;
  /** R2: container types known to the system (isContainer) and the capacities of the offer species for its dry type. */
  containerTypes: ContainerTypeRef[] = [];
  /** Container type of the legacy line: implied by the freight type, or chosen by the user when it cannot be resolved. */
  legacyTypeId: number | null = null;
  /** R4: containers the user wants; prefilled with the computed minimum. */
  legacyCount: number | null = null;
  gridLoaded = false;
  /** The container types arrived: only then can an unresolved freight type be told apart from one not yet resolved. */
  typesLoaded = false;
  form: FormGroup;
  saving = false;
  errorMessage: string | null = null;
  exchangeRate: number | null = null;
  fixedCustomsFee: number | null = null;
  fixedTransportFee: number | null = null;
  insurancePercentage: number | null = null;
  customsPercentage: number | null = null;

  private gridCells: CapacityCellRef[] = [];
  /** Steps already written by a (partially failed) legacy submit: a retry must not repeat them ('Linha ja usa contentores.'). */
  private legacyTypeSaved: number | null = null;
  private legacyCountSaved: number | null = null;
  /** Freight id whose FRETE was written (keyed, so picking another freight after a failure is not skipped). */
  private legacyFreightSavedId: number | null = null;
  private savedCount = 0;
  /** A write succeeded but the refresh after it failed: closing must still tell the caller to refresh. */
  private refreshPending = false;
  private readonly _unsubscribeAll = new Subject<void>();
  private readonly _search$ = new Subject<string>();
  /** Free text over the freight list (company, destination, type, notes, continent); '' = no search. */
  searchText = '';
  private readonly defaultDescription: Partial<Record<CostType, string>>;
  private readonly optionalFieldLabels: Partial<Record<CostType, string>>;

  constructor(
    private readonly fb: FormBuilder,
    private readonly proposalService: ProposalService,
    private readonly shippingService: ShippingService,
    private readonly containerTypesService: ContainerTypesService,
    private readonly speciesCapacityService: SpeciesCapacityService,
    private readonly settingsService: SettingsService,
    private readonly dialogRef: MatDialogRef<DialogAddCostComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      proposal: { id: number };
      productOffer2ProposalId: number;
      originPortId: number;
      /** Destination port of the proposal request, preselected when it has freights. */
      requestDestinyPortId?: number | null;
      /** Ports linked to the proposal clients (Port2Client), the fallback default. */
      clientPortIds?: number[];
      /** The proposal line (ProductOffer2Proposal) with volumeM3, containers snapshot and additionalCosts. */
      line?: LineForFreight;
      product?: {
        specy?: { id?: number | string; appliesImportTax?: boolean };
        dryTypeId?: number | string | null;
        quantity?: any;
      };
      incotermName?: string;
      incotermIncludedCostTypes?: AdditionalCostType[];
      offerBasePrice?: number;
    },
    private readonly _translocoService: TranslocoService,
  ) {
    this.defaultDescription = {
      seguro: this._translocoService.translate('proposals.dialogs.addCost.defaultDescriptions.insurance'),
      desalfandegamento: this._translocoService.translate('proposals.dialogs.addCost.defaultDescriptions.customsClearance'),
      transporte: this._translocoService.translate('proposals.dialogs.addCost.defaultDescriptions.transport'),
      outro: '',
    };

    this.optionalFieldLabels = {
      seguro: this._translocoService.translate('proposals.dialogs.addCost.optionalFieldLabels.insurance'),
      desalfandegamento: this._translocoService.translate('proposals.dialogs.addCost.optionalFieldLabels.customsClearance'),
      transporte: this._translocoService.translate('proposals.dialogs.addCost.optionalFieldLabels.transport'),
      outro: this._translocoService.translate('proposals.dialogs.addCost.optionalFieldLabels.other'),
    };
  }

  /** A line quoted with containers (D7/D8): FRETE is priced per container type by the backend. */
  get isContainerLine(): boolean {
    return (this.data.line?.containers?.length ?? 0) > 0;
  }

  /** A bulk line: priced per m3 at the offer's bulk price, with BULK freights only (tariff per m3, used as is). */
  get isBulkLine(): boolean {
    return this.data.line?.transportMode === 'BULK';
  }

  /** Line volume in m3 as computed by the backend (0 when unknown). */
  get lineVolumeM3(): number {
    return this.data.line?.volumeM3 ?? 0;
  }

  /** R1: the OFFER quantity in m3, read-only here (never an override). */
  get offerVolumeM3(): number {
    return legacyVolumeM3(this.data.product?.quantity ?? (this.data.line as any)?.product?.quantity);
  }

  private get offerSpecyId(): number {
    return Number(this.data.product?.specy?.id ?? (this.data.line as any)?.product?.specy?.id);
  }

  private get offerDryTypeId(): number {
    return Number(this.data.product?.dryTypeId ?? (this.data.line as any)?.product?.dryTypeId);
  }

  /**
   * Legacy line that can be quoted with containers: the offer has species, dry type and a volume. Otherwise (and only then)
   * the manual EUR/m3 flow stays (`manualFallbackReason` says why). A missing capacity never falls back to EUR/m3: the
   * freight is added pending and the line asks the container type (F2).
   */
  get useContainerFlow(): boolean {
    return this.manualFallbackReason === null;
  }

  get manualFallbackReason(): 'noOffer' | null {
    if (this.isContainerLine) {
      return null;
    }
    // A bulk line never moves to containers: its freight is the manual per m3 flow.
    if (this.isBulkLine) {
      return 'noOffer';
    }
    if (!(this.offerSpecyId > 0) || !(this.offerDryTypeId > 0) || !(this.offerVolumeM3 > 0)) {
      return 'noOffer';
    }
    return null;
  }

  private get selectedFreight(): any | null {
    return this.freightOptions.find((o) => o.id === this.selectedFreightId) ?? null;
  }

  /** The freight type already told the container type (R2); otherwise the user must pick it. */
  get typeFromFreight(): boolean {
    const freight = this.selectedFreight;
    return !!freight && resolveFreightContainerTypeId(freight, this.containerTypes.map((t) => t.id)) !== null;
  }

  /**
   * The freight type is not linked to a container type: the FRETE is still added, without value, and the container type
   * is chosen later on the offer line ("pending container type").
   */
  get pendingFreightType(): boolean {
    return this.useContainerFlow && this.typesLoaded && this.selectedFreightId !== null && !this.typeFromFreight;
  }

  get legacyTypeName(): string {
    return this.containerTypes.find((t) => t.id === Number(this.legacyTypeId))?.name ?? '';
  }

  /** Realtime estimate (R3-R5); null until freight, type and capacity are known. */
  get legacyPlan(): LegacyContainerPlan | null {
    const freight = this.selectedFreight;
    if (!freight || this.legacyTypeId === null) {
      return null;
    }
    return legacyContainerPlan(
      this.offerVolumeM3,
      this.legacyTypeId,
      this.gridCells,
      mainPrice(freight).value,
      isValidContainerCount(this.legacyCount, 1) ? this.legacyCount : null,
    );
  }

  /** The freight's container type has no capacity for this species and dry type: the freight is added pending (F2). */
  get legacyNoCapacity(): boolean {
    return this.gridLoaded && this.legacyTypeId !== null && this.selectedFreightId !== null && this.legacyPlan === null;
  }

  /** Names for the "capacity missing for <species> <dry type> in <container>" message. */
  get noCapacityParams(): { species: string; dryType: string; type: string } {
    const product: any = this.data.product ?? (this.data.line as any)?.product ?? {};
    return {
      species: product?.specy?.name ?? '',
      dryType: product?.dryType?.name ?? '',
      type: this.legacyTypeName,
    };
  }

  get legacyCountValid(): boolean {
    const plan = this.legacyPlan;
    return !!plan && isValidContainerCount(this.legacyCount, 1);
  }

  get canSubmitLegacyContainers(): boolean {
    if (this.pendingFreightType || this.legacyNoCapacity) {
      return !this.saving;
    }
    return !this.saving && this.selectedFreightId !== null && this.legacyCountValid;
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  /** Back to the computed minimum (R3) whenever freight or type change. */
  private resetLegacyCount(): void {
    const needed = this.legacyPlan?.needed ?? null;
    this.legacyCount = needed;
  }

  private loadLegacyCapacity(): void {
    this.containerTypesService
      .getList()
      .pipe(take(1))
      .subscribe({
        next: (types) => {
          this.containerTypes = (types ?? []).filter((t) => t.id != null).map((t) => ({ id: +t.id, name: t.name }));
          this.typesLoaded = true;
          // A freight clicked before the types arrived could not be resolved yet: do it now.
          if (this.selectedFreight && this.legacyTypeId === null) {
            this.applyFreightContainerType(this.selectedFreight);
          }
        },
        error: () => (this.containerTypes = []),
      });
    this.speciesCapacityService
      .getGrid(this.offerSpecyId)
      .pipe(take(1))
      .subscribe({
        next: (grid) => {
          this.gridCells = capacityCellsForDryType(grid, this.offerDryTypeId);
          this.gridLoaded = true;
          this.resetLegacyCount();
        },
        error: () => {
          this.gridCells = [];
          this.gridLoaded = true;
        },
      });
  }

  ngOnInit(): void {
    this._search$.pipe(debounceTime(300), takeUntil(this._unsubscribeAll)).subscribe((text) => {
      this.searchText = text.trim();
      this.selectedFreightId = null;
      this.containerBlocks.forEach((state) => (state.selectedFreightId = null));
      this.loadFreights();
    });

    this.form = this.fb.group({
      description: [this.defaultDescription.seguro, Validators.required],
      supplierReference: [''],
      value: [null, [Validators.required, Validators.min(0)]],
    });

    if (this.isContainerLine) {
      this.containerBlocks = buildContainerBlocks(this.data.line).map((block) => ({
        block,
        options: [],
        views: [],
        selectedFreightId: null,
        loading: true,
      }));
    } else {
      if (this.offerSpecyId > 0 && this.offerDryTypeId > 0) {
        this.loadLegacyCapacity();
      }
      this.shippingService.shippings$
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe((options: any[]) => (this.freightOptions = options ?? []));
    }
    this.loadDestinationsThenFreights();

    this.settingsService
      .getCostPercentages()
      .pipe(take(1))
      .subscribe({
        next: (settings) => {
          this.insurancePercentage = settings.insurancePercentage;
          this.customsPercentage = settings.customsPercentage;
        },
        // leave both null; suggestion buttons will compute against 0 rather than crash the dialog
        error: () => {},
      });
  }

  /**
   * Loads the destination ports, preselects the default (request port, else the only client port) and then the
   * freights. A failing destination query keeps "Todos" and never blocks the freight list.
   */
  private loadDestinationsThenFreights(): void {
    const types = new Set(this.containerBlocks.map((state) => state.block.quantityTypeId));
    const quantityTypeId = types.size === 1 ? [...types][0] : undefined;
    this.shippingService
      .getDestinationPorts(this.data.originPortId, quantityTypeId)
      .pipe(take(1))
      .subscribe({
        next: (options) => {
          this.destinationOptions = (options ?? []).filter((o) => o.portId != null);
          this.destinyPortId = defaultDestinationPortId(
            this.destinationOptions,
            this.data.requestDestinyPortId,
            this.data.clientPortIds,
          );
          this.loadFreights();
        },
        error: () => {
          this.destinationOptions = [];
          this.destinyPortId = null;
          this.loadFreights();
        },
      });
  }

  /** Types in the freight search box: re-queries every freight list once the user pauses typing. */
  changeSearch(text: string): void {
    this._search$.next(text ?? '');
  }

  /** Re-queries every freight list of the dialog for another destination port (null = "Todos"). */
  changeDestination(portId: number | null): void {
    this.destinyPortId = portId == null ? null : +portId;
    this.selectedFreightId = null;
    this.containerBlocks.forEach((state) => (state.selectedFreightId = null));
    this.loadFreights();
  }

  /** True when any freight list of the dialog may have been cut by the take. */
  get moreFreights(): boolean {
    return this.isContainerLine ? this.containerBlocks.some((state) => state.moreFreights) : this.legacyMoreFreights;
  }

  private loadFreights(): void {
    if (this.isContainerLine) {
      this.loadContainerBlocks();
      return;
    }
    this.shippingService
      .getList(
        freightQuery({
          originPortId: this.data.originPortId,
          destinyPortId: this.destinyPortId,
          search: this.searchText,
          bulkOnly: this.isBulkLine,
        }),
      )
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((rows) => (this.legacyMoreFreights = mayHaveMoreFreights(rows)));
  }

  private loadContainerBlocks(): void {
    const today = todayISO();
    const containers = this.data.line.containers;
    this.containerBlocks.forEach((state) => {
      state.loading = true;
      this.shippingService
        .getList(
          freightQuery({
            originPortId: this.data.originPortId,
            quantityTypeId: state.block.quantityTypeId,
            destinyPortId: this.destinyPortId,
            search: this.searchText,
          }),
        )
        .pipe(take(1))
        .subscribe({
          next: (rows) => {
            state.options = rows ?? [];
            state.views = state.options.map((freight) =>
              buildContainerFreightOption(freight, state.block, containers, this.lineVolumeM3, today),
            );
            state.moreFreights = mayHaveMoreFreights(rows);
            state.loading = false;
          },
          error: () => {
            state.loading = false;
            this.errorMessage = this._translocoService.translate('proposals.dialogs.addCost.loadFreightError');
          },
        });
    });
  }

  trackByContainerType(_index: number, state: ContainerBlockState): number {
    return state.block.quantityTypeId;
  }

  optionalFieldLabel(tab: CostType): string {
    return this.optionalFieldLabels[tab] ?? '';
  }

  selectTab(tab: CostType): void {
    this.activeTab = tab;
    this.selectedFreightId = null;
    this.legacyFreightValue = null;
    this.legacyTypeId = null;
    this.legacyCount = null;
    this.containerBlocks.forEach((state) => (state.selectedFreightId = null));
    if (tab !== 'frete') {
      this.form.patchValue({ description: this.defaultDescription[tab] ?? '', value: null, supplierReference: '' });
    }
  }

  selectTransportMode(mode: TransportMode): void {
    this.transportMode = mode;
    this.selectedFreightId = null;
    this.legacyFreightValue = null;
    this.legacyTypeId = null;
    this.legacyCount = null;
  }

  get filteredFreightOptions(): FreightOption[] {
    const withMode = (this.freightOptions ?? []).map((o) => ({ ...o, transportMode: this.viaToMode(o.via) }));
    return filterFreightByMode(withMode, this.transportMode);
  }

  private viaToMode(via: string): TransportMode {
    const fromVia: Record<string, TransportMode> = { SEA: 'maritimo', AIR: 'ar', LAND: 'terra' };
    return fromVia[via] ?? 'maritimo';
  }

  /** Manual flow (non container lines): picking a row prefills the editable EUR/m3 value. */
  selectFreight(option: any): void {
    this.selectedFreightId = option.id;
    this.legacyFreightValue = legacyFreightValuePerM3(mainPrice(option).value, this.lineVolumeM3, option);
    this.applyFreightContainerType(option);
  }

  /** R2: the freight type implies the container type; otherwise the user is asked. */
  private applyFreightContainerType(option: any): void {
    const fromFreight = resolveFreightContainerTypeId(option, this.containerTypes.map((t) => t.id));
    this.legacyTypeId = fromFreight ?? (this.legacyTypeSaved !== null ? this.legacyTypeSaved : null);
    this.resetLegacyCount();
  }

  selectContainerFreight(state: ContainerBlockState, option: any): void {
    state.selectedFreightId = option.id;
  }

  get addsCostToPriceNote(): string {
    return this._translocoService.translate('proposals.dialogs.addCost.addsCostNote');
  }

  private get includedCostTypes(): AdditionalCostType[] {
    return this.data.incotermIncludedCostTypes ?? [];
  }

  get showImportTaxSuggestion(): boolean {
    return (
      this.activeTab === 'desalfandegamento' &&
      !!this.data.product?.specy?.appliesImportTax &&
      this.includedCostTypes.includes('DESALFANDEGAMENTO')
    );
  }

  get showInsuranceSuggestion(): boolean {
    return this.activeTab === 'seguro' && this.includedCostTypes.includes('SEGURO');
  }

  get showTransportSuggestion(): boolean {
    return this.activeTab === 'transporte' && this.includedCostTypes.includes('TRANSPORTE');
  }

  /**
   * A non freight cost is typed as the TOTAL for the line; the backend divides it by the line volume. This previews
   * that per m3 value (full precision) for display only; the saved cost shows the backend number.
   */
  get costPerM3Preview(): number | null {
    const total = this.form?.get('value')?.value;
    if (total == null || total === '' || !Number.isFinite(+total) || this.lineVolumeM3 <= 0) {
      return null;
    }
    return +total / this.lineVolumeM3;
  }

  suggestValue(): void {
    const containerVolumeM3 = this.lineVolumeM3;
    const offerBasePrice = this.data.offerBasePrice ?? 0;

    if (this.activeTab === 'desalfandegamento') {
      const input: ImportTaxCostInput = {
        appliesImportTax: !!this.data.product?.specy?.appliesImportTax,
        includedCostTypes: this.includedCostTypes,
        offerBasePrice,
        containerVolumeM3,
        exchangeRate: this.exchangeRate ?? 0,
        fixedCustomsFee: this.fixedCustomsFee ?? 0,
        customsPercentage: this.customsPercentage ?? 0,
      };
      const suggested = suggestImportTaxCost(input);
      if (suggested != null) {
        this.setSuggestedTotal(suggested);
      }
      return;
    }

    if (this.activeTab === 'seguro') {
      const suggested = suggestInsuranceCost(offerBasePrice, this.insurancePercentage ?? 0);
      this.setSuggestedTotal(suggested);
      return;
    }

    if (this.activeTab === 'transporte') {
      const suggested = suggestTransportCost(this.fixedTransportFee ?? 0, containerVolumeM3, this.exchangeRate ?? 0);
      if (suggested != null) {
        this.setSuggestedTotal(suggested);
      }
    }
  }

  /** The suggestion helpers return a per m3 amount; the field is the line TOTAL (per m3 x line volume). */
  private setSuggestedTotal(perM3: number): void {
    this.form.get('value').setValue(round2(perM3 * this.lineVolumeM3));
  }

  submit(): void {
    this.errorMessage = null;

    if (this.activeTab === 'frete') {
      if (this.isContainerLine) {
        this.submitContainerFreight();
      } else {
        this.submitLegacyFreight();
      }
      return;
    }

    if (this.form.invalid) return;
    this.saving = true;
    this.proposalService
      .addAdditionalCostToOffer(this.data.productOffer2ProposalId, {
        type: this.activeTab,
        description: this.form.value.description,
        totalAmount: this.form.value.value,
        supplierReference: this.form.value.supplierReference || undefined,
      })
      .subscribe({ next: () => this.refreshAndClose(), error: (err) => this.onSaveError(err) });
  }

  /**
   * Container line: one write per chosen container type, strictly one after the other (each write makes the backend
   * recompute the whole proposal, so concurrent writes would race). No value is sent: the backend reads the freight
   * price and derives the per m3 value from the line snapshot (D10).
   */
  private submitContainerFreight(): void {
    // Blocks already written in an earlier (partially failed) submit are skipped unless the user picked another freight.
    const chosen = this.containerBlocks.filter(
      (state) => state.selectedFreightId != null && state.selectedFreightId !== state.savedFreightId,
    );
    if (chosen.length === 0) {
      if (this.savedCount > 0) {
        this.saving = true;
        this.refreshAndClose();
      }
      return;
    }

    const description = this._translocoService.translate('proposals.freight.description');
    this.saving = true;
    from(chosen)
      .pipe(
        concatMap((state) =>
          (state.block.existingCost
            ? this.proposalService.updateAdditionalCostFreight(state.block.existingCost.id, state.selectedFreightId)
            : this.proposalService.addAdditionalCostToOffer(this.data.productOffer2ProposalId, {
                type: 'frete',
                description,
                freightId: state.selectedFreightId,
              })
          ).pipe(tap((saved: any) => this.markBlockSaved(state, saved))),
        ),
        toArray(),
      )
      .subscribe({ next: () => this.refreshAndClose(), error: (err) => this.onSaveError(err) });
  }

  /** After a successful write the block owns a FRETE cost: a retry must update it, never add a duplicate. */
  private markBlockSaved(state: ContainerBlockState, saved: any): void {
    this.savedCount++;
    state.savedFreightId = state.selectedFreightId;
    const costId = state.block.existingCost?.id ?? saved?.id;
    if (costId != null) {
      state.block.existingCost = {
        ...(state.block.existingCost ?? {}),
        id: costId,
        type: 'frete',
        basisQuantityTypeId: state.block.quantityTypeId,
        freight: { ...(state.block.existingCost?.freight ?? {}), id: state.selectedFreightId },
      };
    }
  }

  /** Non container line (D8): manual EUR/m3 value. TODO(OQ5): the unit of LCL/BULK tariffs is unknown. */
  private submitLegacyFreight(): void {
    if (this.useContainerFlow) {
      this.submitLegacyContainers();
      return;
    }
    const selected = this.freightOptions.find((o) => o.id === this.selectedFreightId);
    if (!selected) return;
    const value = this.legacyFreightValue;
    if (value == null || !Number.isFinite(+value) || +value < 0) return;

    this.saving = true;
    this.proposalService
      .addAdditionalCostToOffer(this.data.productOffer2ProposalId, {
        type: 'frete',
        description: this._translocoService.translate('proposals.freight.description'),
        value: +value,
        freightId: selected.id,
      })
      .subscribe({ next: () => this.refreshAndClose(), error: (err) => this.onSaveError(err) });
  }

  /** Unlinked freight type on a legacy line: no conversion, no value; the container type is assigned on the offer line. */
  private submitPendingFreight(): void {
    if (!this.canSubmitLegacyContainers) {
      return;
    }
    this.saving = true;
    this.proposalService
      .addAdditionalCostToOffer(+this.data.productOffer2ProposalId, {
        type: 'frete',
        description: this._translocoService.translate('proposals.freight.description'),
        freightId: this.selectedFreightId,
      })
      .subscribe({ next: () => this.refreshAndClose(), error: (err) => this.onSaveError(err) });
  }

  /**
   * R2-R4 for a legacy line: container type -> (count when above the computed minimum) -> FRETE without value (D10).
   * Strictly sequential; each finished step is remembered so a retry after a partial failure never converts twice.
   */
  private submitLegacyContainers(): void {
    if (this.pendingFreightType || this.legacyNoCapacity) {
      this.submitPendingFreight();
      return;
    }
    const plan = this.legacyPlan;
    if (!this.canSubmitLegacyContainers || !plan) {
      return;
    }
    const lineId = +this.data.productOffer2ProposalId;
    const typeId = plan.quantityTypeId;
    this.saving = true;

    // The server line state is (legacyTypeSaved, legacyCountSaved): a retry only writes what differs from it. A type chosen
    // after the conversion already happened swaps the snapshot through setLineContainers (setLineContainerType would
    // answer 'Linha ja usa contentores.').
    const convert$: Observable<unknown> =
      this.legacyTypeSaved !== null
        ? of(null)
        : this.proposalService.setLineContainerType(lineId, typeId).pipe(
            tap(() => {
              this.legacyTypeSaved = typeId;
              this.legacyCountSaved = plan.needed;
            }),
          );
    const count$ = (): Observable<unknown> =>
      this.legacyTypeSaved === typeId && this.legacyCountSaved === plan.count
        ? of(null)
        : this.proposalService.setLineContainers(lineId, [{ quantityTypeId: typeId, count: plan.count }]).pipe(
            tap(() => {
              this.legacyTypeSaved = typeId;
              this.legacyCountSaved = plan.count;
            }),
          );
    const freightId = this.selectedFreightId;
    const freight$ = (): Observable<unknown> =>
      this.legacyFreightSavedId === freightId
        ? of(null)
        : this.proposalService
            .addAdditionalCostToOffer(lineId, {
              type: 'frete',
              description: this._translocoService.translate('proposals.freight.description'),
              freightId,
            })
            .pipe(tap(() => (this.legacyFreightSavedId = freightId)));

    convert$
      .pipe(
        concatMap(() => count$()),
        concatMap(() => freight$()),
      )
      .subscribe({ next: () => this.refreshAndClose(), error: (err) => this.onSaveError(err) });
  }

  private onSaveError(err?: unknown): void {
    this.saving = false;
    this.errorMessage =
      backendErrorMessage(err) ?? this._translocoService.translate('proposals.dialogs.addCost.saveError');
  }

  private refreshAndClose(): void {
    this.proposalService.getByIdToGetProducts(this.data.proposal.id).subscribe({
      next: (res) => {
        this.saving = false;
        if (res?.ProductOffer2Proposal) {
          res.ProductOffer2Proposal.forEach((element: any) => {
            this.proposalService.products = element;
          });
        }
        this.dialogRef.close(true);
      },
      error: () => {
        this.saving = false;
        this.refreshPending = true;
        this.errorMessage = this._translocoService.translate('proposals.dialogs.addCost.refreshError');
      },
    });
  }

  /** Closing after a partial container save (first write ok, a later one rejected) still tells the caller to refresh. */
  cancel(): void {
    this.dialogRef.close(this.savedCount > 0 || this.legacyTypeSaved !== null || this.refreshPending);
  }
}
